import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { Doughnut } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Function to interpolate color between green and red based on vuln value
const interpolateColor = (vuln) => {
  const vulnPercentage = vuln * 100;
  if (vulnPercentage >= 20) {
    return "#FF0000";
  }
  const r = Math.round(0 + 255 * (vulnPercentage / 20));
  const g = Math.round(255 - 255 * (vulnPercentage / 20));
  const b = 0;
  return `rgb(${r}, ${g}, ${b})`;
};

// Responsive StageBox
const StageBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  borderRadius: 8,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  textAlign: "center",
  width: { sm: "95%", xs: "90%" },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

// Responsive DescriptionBox
const DescriptionBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  borderRadius: 8,
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  width: "85%",
  maxWidth: 300,
  minHeight: 200,
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    marginLeft: 0,
    marginTop: theme.spacing(2),
  },
}));

export default function ProbaDetails() {
  const dashboardData = useSelector((state) => state.dashboard.jsonData);

  const history = useHistory();
  const { t } = useTranslation();

  // Initialize with safe defaults
  const probaData = dashboardData?.proba_values || {
    data: [],
    level: {
      p1: 0,
      p2: 0,
      p3: 0,
      p4: 0,
      p5: 0,
      reconnaissance: 0,
      "resource-development": 0,
      "initial-access": 0,
      execution: 0,
      persistence: 0,
      "privilege-escalation": 0,
      "defense-evasion": 0,
      "credential-access": 0,
      discovery: 0,
      "lateral-movement": 0,
      collection: 0,
      "command-and-control": 0,
      exfiltration: 0,
      impact: 0,
    },
  };

  const [selectedThreat, setSelectedThreat] = useState("");

  // Set default threat once data is loaded
  useEffect(() => {
    if (probaData.data.length > 0 && !selectedThreat) {
      setSelectedThreat(probaData.data[0]?.threat || "");
    }
  }, [probaData.data, selectedThreat]);

  function handleChange(event) {
    setSelectedThreat(event.target.value);
  }

  const displayedData =
    selectedThreat && probaData.data.length > 0
      ? probaData.data.find((item) => item.threat === selectedThreat)
      : probaData.data[0] || { threat: "", justification: "", min: 0, max: 0 };

  function handlGoBack() {
    history.goBack();
  }

  // // Don't render if no data is available yet
  // if (probaData.data.length === 0) {
  //     return (
  //         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
  //             <Typography variant="h6">No data available</Typography>
  //         </Box>
  //     );
  // }

  const stages = [
    {
      name: "Initial Access",
      probability: probaData.level.p1 * 100,
      tactics: {
        reconnaissance: probaData.level.reconnaissance,
        "resource-development": probaData.level["resource-development"],
        "initial-access": probaData.level["initial-access"],
      },
    },
    {
      name: "Execution",
      probability: probaData.level.p2 * 100,
      tactics: {
        execution: probaData.level.execution,
        persistence: probaData.level.persistence,
        "privilege-escalation": probaData.level["privilege-escalation"],
      },
    },
    {
      name: "Persistence",
      probability: probaData.level.p3 * 100,
      tactics: {
        "defense-evasion": probaData.level["defense-evasion"],
        "credential-access": probaData.level["credential-access"],
        discovery: probaData.level.discovery,
      },
    },
    {
      name: "Privilege Escalation",
      probability: probaData.level.p4 * 100,
      tactics: {
        "lateral-movement": probaData.level["lateral-movement"],
        collection: probaData.level.collection,
        "command-and-control": probaData.level["command-and-control"],
      },
    },
    {
      name: "Impact",
      probability: probaData.level.p5 * 100,
      tactics: {
        exfiltration: probaData.level.exfiltration,
        impact: probaData.level.impact,
      },
    },
  ];

  // Gauge configuration using the threat's min/max values
  const gaugeData = {
    datasets: [
      {
        data: [displayedData.max * 100, 100 - displayedData.max * 100],
        backgroundColor: [interpolateColor(displayedData.max), "grey"],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
      {
        data: [displayedData.max * 100, 2, 100 - displayedData.max * 100 - 2],
        backgroundColor: ["transparent", "#000000", "transparent"],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  const gaugeOptions = {
    plugins: {
      datalabels: {
        display: false,
      },
      legend: { display: false },
      tooltip: { enabled: false },
    },
    cutout: "80%",
    rotation: -90,
    circumference: 180,
    maintainAspectRatio: false,
  };

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 3, md: 4 },
        width: "85%",
        maxWidth: "90%",
        margin: "0 auto",
        marginBottom: "70px",
      }}
    >
      <Button
        sx={{
          background: "#78c3ae",
          borderRadius: 4,
          fontSize: "16px",
          marginLeft: { xs: 0, sm: "5px" },
          alignSelf: "flex-start",
          "&:hover": {
            backgroundColor: "#60dbb9",
          },
        }}
        onClick={handlGoBack}
      >
        <ArrowBackIcon sx={{ color: "white" }} />
      </Button>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{
          fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
        }}
      >
        Probability of successful attack
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "#e0e0e0",
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          bgcolor: "background.paper",
          width: {
            xs: "90%",
            sm: "90%",
            md: "90%",
          },
          flex: 1,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 40,
          marginBottom: 5,
          marginTop: 3,
        }}
      >
        <FormControl fullWidth>
          <InputLabel
            id="threat-select-label"
            sx={{ fontSize: { xs: "1rem", sm: "1rem" } }}
          >
            Threat Type
          </InputLabel>
          <Select
            labelId="threat-select-label"
            id="threat-select"
            value={selectedThreat}
            label="Threat Type"
            onChange={handleChange}
            sx={{
              height: { xs: "52px", sm: "64px" },
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {probaData.data.map((threat, index) => (
              <MenuItem
                key={index}
                value={threat.threat}
                sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
              >
                {t(threat.threat)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ alignItems: { xs: "center", md: "flex-start" } }}
      >
        {/* Flowchart Section */}
        <Box sx={{ flexGrow: 1, width: "100%" }}>
          {stages.map((stage, index) => (
            <React.Fragment key={stage.name}>
              <StageBox>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  {stage.name} : {stage.probability.toFixed(0)}%
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="center"
                  mt={1}
                >
                  {Object.entries(stage.tactics).map(([tactic, count]) => (
                    <Typography
                      key={tactic}
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      {tactic
                        .replace(/-/g, " ")
                        .replace(/(^|\s)\S/g, (l) => l.toUpperCase())}{" "}
                      : {count}
                    </Typography>
                  ))}
                </Stack>
              </StageBox>
              {index < stages.length - 1 && (
                <Box sx={{ textAlign: "center", my: 1 }}>
                  <ArrowDownwardIcon
                    sx={{
                      fontSize: { xs: "24px", sm: "30px" },
                      color: "grey.600",
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}

          {/* Gauge Chart */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              Overall Attack Success Probability
            </Typography>
            <Box
              sx={{
                width: { xs: 120, sm: 150, md: 150 },
                height: { xs: 60, sm: 75, md: 75 },
                margin: "0 auto",
                position: "relative",
              }}
            >
              <Doughnut data={gaugeData} options={gaugeOptions} />
              <Typography
                variant="body1"
                sx={{
                  position: "absolute",
                  top: "80%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "black",
                  fontWeight: "bold",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  whiteSpace: "nowrap",
                  zIndex: 1,
                }}
              >
                [{(displayedData.min * 100).toFixed(0)}% —{" "}
                {(displayedData.max * 100).toFixed(0)}%]
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description Box */}
        <DescriptionBox>
          <Typography variant="h6" gutterBottom>
            {displayedData.threat
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            {displayedData.justification}
          </Typography>
        </DescriptionBox>
      </Stack>
    </Box>
  );
}
